import { Button } from '@/components/ui/button';
import { PrimaryHoverCard } from '@/components/ui/primary-hover-card';
import { TextInputField } from '@/components/ui/text-input-field';
import { View } from '@/components/ui/tw-wrappers';
import { Colors } from '@/constants/theme';
import { useCountryNameById } from '@/hooks/useCountryName';
import { functions, httpsCallable, track } from '@/lib/firebase';
import { useBenchmarkingStore } from '@/stores/benchmarking-store';
import { useScenarioStore } from '@/stores/scenario-store';
import { Country } from '@/types/country';
import { ModelType, SendReportRequestData } from '@/types/model';
import { ReportFormValues, reportSchema } from '@/types/reportSchema';
import type { TransferPair } from '@/types/transfer-pair';
import { useFormatters } from '@/utils/formatters/useFormatters';
import { buildBenchmarkingPayload, buildComparePayload } from '@/utils/report-payloads';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { Text } from './text';
import { IconSymbol } from './ui/icon-symbol';

export function ReportForm({
  data,
  countries,
  disabled,
  modelType,
}: {
  data?: TransferPair | null;
  countries: Country[];
  disabled?: boolean;
  modelType: ModelType;
}) {
  const { t } = useTranslation();

  const { formatCurrency, formatPercentage, formatNumber } = useFormatters();
  const formatters = { formatCurrency, formatPercentage, formatNumber };
  const inputs = useScenarioStore(s => s.inputs);
  const visibleScenarios = useScenarioStore(s => s.visibleScenarios);

  const getCountryName = useCountryNameById(countries);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReportFormValues>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      name: '',
      email: '',
    },
    mode: 'onTouched',
  });

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const onSubmit = async (values: ReportFormValues) => {
    setLoading(true);
    setMessage(null);

    try {
      const sendReport = httpsCallable<SendReportRequestData, unknown>(functions, 'emailReport');

      if (modelType === 'compare') {
        if (!data) throw new Error('Data is required for comparing models');
        const payload = await buildComparePayload({
          values,
          inputs,
          data,
          visibleScenarios,
          getCountryName,
          t,
          formatters,
        });
        await sendReport({ email: values.email, emailPayload: payload, modelType: 'compare' });
        track('model_comparison_report_sent', {
          email: values.email,
          emailPayload: payload,
          modelType: 'compare',
        });
      } else {
        const benchmarkingStore = useBenchmarkingStore.getState();
        const payload = await buildBenchmarkingPayload({
          values,
          benchmarkingCommon: benchmarkingStore.commonProps,
          benchmarkingItems: benchmarkingStore.items,
          getCountryName,
          t,
          formatters,
        });
        await sendReport({ email: values.email, emailPayload: payload, modelType: 'benchmarking' });
        track('benchmarking_report_sent', {
          email: values.email,
          emailPayload: payload,
          modelType: 'benchmarking',
        });
      }

      setMessage({ text: t('home.reportForm.success'), type: 'success' });
      reset();
    } catch (err) {
      console.error('Error calling sendModelCompareReport:', err);
      setMessage({ text: t('home.reportForm.error'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.outerContainer}>
      <PrimaryHoverCard
        backgroundColor='#fff'
        borderColor='#E5E7EB'
        borderWidth={2}
        className='h-full'>
        <View style={styles.header}>
          <View className='flex-row gap-4 items-center'>
            <View style={[styles.iconContainer, { width: 48, height: 48, borderRadius: 24 }]} className='self-center'>
              <IconSymbol name={'file-open'} size={24} color={Colors.light.primary} />
            </View>
            <Text type='defaultBold' style={styles.title}>
              {t('home.reportForm.title')}
            </Text>
          </View>
          <Text style={styles.description}>{t('home.reportForm.description')}</Text>
        </View>

        <View style={styles.formContainer}>
          <View className='flex-col gap-4 w-full md:flex-row md:items-start md:justify-between'>
            <View className='flex-col gap-4 w-full md:flex-row md:flex-1'>
              <View className='flex-col gap-1 w-full md:flex-1'>
                <Controller
                  control={control}
                  name='name'
                  render={({ field: { value, onChange } }) => (
                    <TextInputField
                      id='name'
                      name='name'
                      label={t('home.reportForm.nameLabel')}
                      placeholder={t('home.reportForm.namePlaceholder')}
                      placeholderTextColor={Colors.light.placeholderText}
                      value={value}
                      onChangeText={onChange}
                      disabled={loading}
                    />
                  )}
                />
                {errors.name && <Text style={styles.fieldError}>{errors.name.message}</Text>}
              </View>

              <View className='flex-col gap-1 w-full md:flex-1'>
                <Controller
                  control={control}
                  name='email'
                  render={({ field: { value, onChange } }) => (
                    <TextInputField
                      id='email'
                      name='email'
                      label={t('home.reportForm.emailLabel')}
                      placeholder={t('home.reportForm.emailPlaceholder')}
                      placeholderTextColor={Colors.light.placeholderText}
                      value={value}
                      onChangeText={onChange}
                      disabled={loading}
                    />
                  )}
                />
                {errors.email && <Text style={styles.fieldError}>{errors.email.message}</Text>}
              </View>
            </View>
          </View>
          <View style={{ minHeight: 50 }}>
            {message && (
              <Animated.View
                entering={FadeIn}
                exiting={FadeOut}
                style={[styles.message, message.type === 'error' ? styles.error : styles.success]}>
                <Text style={{ color: message.type === 'error' ? '#991B1B' : '#065F46', textAlign: 'center' }}>
                  {message.text}
                </Text>
              </Animated.View>
            )}
          </View>
          <View className='w-full md:w-auto md:min-w-[160px]  md:items-start'>
            <Button
              label={loading ? t('common.sending') : t('home.reportForm.submitButton')}
              onPress={handleSubmit(onSubmit)}
              disabled={loading || disabled}
              style={styles.button}
              outlinedUntilHover
            />
          </View>
        </View>
      </PrimaryHoverCard>
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    width: '100%',
    height: '100%',
  },
  card: {
    width: '100%',
    padding: 0, // Reset default Card padding to control it precisely inside
  },
  header: {
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#11181C',
  },
  description: {
    fontSize: 14,
    color: Colors.light.secondaryText,
    lineHeight: 22,
  },
  formContainer: {
    gap: 16,
  },
  button: {
    height: 48,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  message: {
    padding: 12,
    borderRadius: 8,
    // marginTop: 8,
  },
  success: {
    backgroundColor: '#D1FAE5',
  },
  error: {
    backgroundColor: '#FEE2E2',
  },
  fieldError: {
    color: '#DC2626',
    fontSize: 12,
    marginLeft: 4,
    marginTop: 4,
  },
  iconContainer: {
    backgroundColor: 'rgba(11, 95, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
